<script setup lang="ts">
import { onBeforeMount, ref, watch } from 'vue';
import { Capacitor } from '@capacitor/core';
import router from '@/router';
import { useRoute } from 'vue-router';
import { IonContent, IonIcon } from '@ionic/vue';
import { chevronBackOutline, mailOutline, closeOutline, callOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth/authStore';
import useAuth from '@/modules/auth/composables/useAuth';
import CustomButton from '@/shared/components/CustomButton.vue';
import ModalThree from '@/shared/components/ui/ModalThree.vue';
import SignInSignUpAlert from '@/shared/components/SignInSignUpAlert.vue';
import SocialLoginButtons from '@/modules/auth/components/SocialLoginButtons.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useTenant from '@/shared/composables/useTenant';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import ShareAppButton from '@/shared/components/share/ShareAppButton.vue';
import InstallApp from '@/shared/components/install/InstallApp.vue';
import { useCacheImage } from '@/shared/composables/useCacheImage';
import useAuthHelpers from '@/modules/auth/composables/useAuthHelpers';
import { SocialProvider } from '@/shared/interfaces/user';
import PreviousLoginModal from '@/modules/auth/components/PreviousLoginModal.vue';

// Init
const history = window.history;
const assetStore = useAssetsStore();
const authStore = useAuthStore();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig, anySocialLoginEnabled, tenantStyles } = getTenantConfigAndStyles();
const { setRedirect } = useAuth();
const route = useRoute();
const { t } = useI18n();
const { loadImage } = useCacheImage();
const logoUrl = ref();
const { detectPreviousLoginMethod } = useAuthHelpers();

const previousLogin = ref<{ provider: string; timestamp: string } | null>(null);
const showPreviousLoginModal = ref(false);

const isSocialProvider = (provider: string) => {
	return ['google', 'facebook', 'apple'].includes(provider);
};

const handleLoginWithPrevious = async () => {
	if (!previousLogin.value) return;

	const { provider } = previousLogin.value;

	if (isSocialProvider(provider)) {
		const { oAuthLogin } = useAuth();
		if (tenantStyles.value) {
			await oAuthLogin(provider as SocialProvider, tenantStyles.value);
			await router.push('/dashboard');
		}
	} else {
		console.warn('Previous login method is not a social provider, redirecting to sign-in home.');
		await router.push('/auth/signinhome?previousLogin=true');
	}
};

const handleUseOtherMethod = () => {
	showPreviousLoginModal.value = false;
};

onBeforeMount(async () => {
	await authStore.authenticateWithSavedToken();
	if (authStore.isAuthenticated()) {
		await router.push('dashboard');
	} else {
		const detected = await detectPreviousLoginMethod();
		if (detected) {
			previousLogin.value = detected;
			showPreviousLoginModal.value = true;
		}
	}
});

watch(
	assetStore,
	async (assetStore) => {
		if (assetStore.logoUrl) {
			let logoImage = assetStore.logoUrl.split('/').pop();
			logoImage = logoImage ?? 'logo';
			logoUrl.value = await loadImage('logo', logoImage, assetStore.logoUrl);
		}
	},
	{ immediate: true },
);

// Save redirection to use it when the register / login process ends
if (route.query.callbackPage && route.query.id) {
	setRedirect({
		callbackPage: route.query.callbackPage.toString(),
		id: route.query.id.toString(),
	});
}

// Modals
const modalState = ref(false);
const modalType = ref('');
const setModal = (state: boolean, type?: string) => {
	modalState.value = state;
	modalType.value = type === 'tos' ? 'tos' : 'pp';
};

// onLoginSuccess
const onLoginSuccess = () => {
	router.push('dashboard');
};

// goTo
const goTo = async (link: string) => {
	await router.push(link);
};

// Go back
const goBack = () => {
	router.back();
};
</script>

<template>
	<ion-modal :is-open="showPreviousLoginModal" :backdrop-dismiss="true" class="modal-wrapper">
		<div class="bg-white rounded-xl p-6 mx-3 flex flex-col">
			<PreviousLoginModal :previous-login="previousLogin" @continue-with-previous="handleLoginWithPrevious" @use-other="handleUseOtherMethod" @on-login-success="onLoginSuccess" />
			<div class="flex w-full items-center justify-center mt-4">
				<div class="flex items-center justify-center w-[40px] h-[40px] rounded-full bg-anykrowd-gray/30">
					<ion-icon class="text-[24px] text-black cursor-pointer" :icon="closeOutline" @click="handleUseOtherMethod" />
				</div>
			</div>
		</div>
	</ion-modal>

	<ion-content>
		<ModalThree :is-open="modalState">
			<div class="flex w-full items-center justify-center mb-4">
				<div class="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-anykrowd-gray/30 mb-4">
					<ion-icon class="text-[38px] text-black cursor-pointer" :icon="closeOutline" @click="setModal(false)"></ion-icon>
				</div>
			</div>
			<h1 class="font-sourcesanspro-semibold text-lg">
				{{ modalType === 'tos' ? t('form.terms_and_conditions') : t('form.privacy_policy') }}
			</h1>
			<div v-dompurify-html="modalType === 'tos' ? tenantConfig!.terms_and_conditions : tenantConfig!.privacy_policy" class="text-anykrowd-dark"></div>
		</ModalThree>

		<div class="flex flex-col justify-between">
			<TopBar v-if="tenantConfig && tenantConfig.enable_public_mode" class="flex px-[34px]">
				<template #left>
					<div class="flex items-center justify-start cursor-pointer">
						<ion-button class="w-[35px] h-[35px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full" shape="round" @click="history.state.back ? goBack() : goTo('dashboard')">
							<ion-icon :icon="chevronBackOutline" class="ion-no-padding ion-no-margin text-black mr-[2px]"></ion-icon>
						</ion-button>
					</div>
				</template>
			</TopBar>
			<div class="flex flex-col h-full justify-center px-[34px]">
				<div class="icon flex justify-center pt-20">
					<img :src="logoUrl" class="max-w-[180px]" />
				</div>
				<div class="pb-4 pt-6 mb-2 rounded-md">
					<h1 class="font-sourcesanspro-bold text-white text-md text-center">
						{{ tenantConfig?.welcome_title }}
					</h1>
					<div class="max-w-xs mx-auto mb-6 text-white text-sm text-center">
						{{ tenantConfig?.welcome_line }}
					</div>
					<div class="flex flex-col justify-center">
						<CustomButton @on-click="goTo('/auth/signupwithemail')" text="auth.sign_up_home_page.signUpEmail" :iconLeft="mailOutline" />
					</div>
				</div>
				<div class="separator mb-6" v-if="anySocialLoginEnabled() || tenantConfig?.enable_phone_login">
					<h5>
						<span class="font-sourcesanspro-regular text-base text-white">{{ $t(`auth.sign_up_home_page.orSignUpWith`) }}</span>
					</h5>
				</div>
				<div class="mb-4 rounded-md">
					<div v-if="tenantConfig?.enable_phone_login" class="flex justify-center mt-2 mb-2">
						<CustomButton class="ion-no-margin" fill="outline" @on-click="goTo('/auth/signupwithphone')" text="auth.sign_up_home_page.signUpPhone" :iconLeft="callOutline" />
					</div>
					<SocialLoginButtons @on-login-success="onLoginSuccess"></SocialLoginButtons>
				</div>
				<div class="px-4 pb-2 rounded-md">
					<SignInSignUpAlert buttonText="components.sign_in.buttonText" text="components.sign_in.text" link="/auth/signinhome" />
				</div>
				<ShareAppButton />
				<InstallApp />
			</div>
		</div>
	</ion-content>
</template>

<style scoped>
ion-modal.modal-wrapper {
	--width: fit-content;
	--height: fit-content;
	--max-width: 500px;
	--border-radius: 16px;
	--box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
}
</style>
